import React from 'react';
import { connect } from 'react-redux';
import Head from 'next/head';

import {
  REVIEWS_PER_STANDARD_PAGE,
  REVIEWS_PER_WIDGET_PAGE,
  STANDARD_REVIEWS_PAGE,
} from 'constants/Profiles';
import {
  getProfile,
  loadMoreReviews,
  selectLoadMoreReviewsIsLoading,
  selectProfile,
  selectProfileError,
  selectProfileHasError,
  selectProfileIsLoaded,
} from 'state/modules/profiles';
import waitForSaga from 'helpers/waitForSaga';
import { profileReviewsPageViewProps } from 'helpers/pageViewProps/profile';
import usePageEffects from 'helpers/hooks/usePageEffects';
import { StandardReviewsPage, WidgetReviewsPage } from 'views/ProfileReviewsPage';
import pick from 'lodash.pick';
import MetaDataMarkup from 'components/MetaDataMarkup';
import { checkForVendorReviewsRedirect } from 'helpers/profiles/redirects';
import {
  getProfileReviewsPageType,
  getProfileReviewsMetaDataProps,
} from 'helpers/profiles/reviews';
import buildPageAnalytics from 'helpers/buildPageAnalytics';
import { gmConfig } from 'helpers/config';
import { PROFILE_REVIEWS_PAGE_NAME } from 'constants/pageNames';
import configureStore from 'state/configureStore';
import getServerSidePropsWrapper from 'helpers/getServerSidePropsWrapper';

export const Reviews = (props) => {
  const { profile, pageType } = props;
  usePageEffects();

  const reviewsMetaDataProps = getProfileReviewsMetaDataProps(profile);
  const isWidgetPage = pageType !== STANDARD_REVIEWS_PAGE;
  const PageComponent = isWidgetPage ? WidgetReviewsPage : StandardReviewsPage;

  return (
    <>
      {isWidgetPage && (
        <Head>
          <script
            defer
            src={`https://${gmConfig.domains.cdn}/Content/scripts/iframeResizer.contentWindow.min.js`}
            type="text/javascript"
          />
        </Head>
      )}
      <MetaDataMarkup {...reviewsMetaDataProps} />
      <PageComponent {...props} />
    </>
  );
};

const getErrorStatusCode = (store, directory) => {
  const profileStatusIsError = selectProfileHasError(store.getState(), directory);

  if (profileStatusIsError) {
    const { statusCode } = selectProfileError(store.getState(), directory);

    if (statusCode) return statusCode;
    return;
  }

  return null;
};

const validReviewsRedirectParams = ['page', 'widget'];
const validReviewsParams = ['page', 'widget', 'dir1', 'dir2'];

export const handleServerSideProps = async (ctx) => {
  const { query: rawQuery, store } = ctx;
  // This is the key we'll use to store things in state
  const page = parseInt(rawQuery.page, 10) || 1;
  const { dir1, dir2 } = rawQuery;
  const query = pick({ ...rawQuery }, validReviewsParams);
  const redirectQuery = pick({ ...rawQuery }, validReviewsRedirectParams);
  const userFacingQuery = { ...query, page };
  const { widget } = userFacingQuery;

  const directory = `/${dir1}/${dir2}`;
  const pageType = getProfileReviewsPageType(widget);
  const profileLoaded = selectProfileIsLoaded(store.getState(), directory);
  const reviewsLoaded = selectLoadMoreReviewsIsLoading(store.getState(), directory, page, widget);

  if (!profileLoaded) store.dispatch(getProfile(directory));
  if (!reviewsLoaded) {
    // Do not skip if it is the Reviews Widget
    const skip = pageType === STANDARD_REVIEWS_PAGE ? (page - 1) * REVIEWS_PER_STANDARD_PAGE : 0;
    const take =
      pageType === STANDARD_REVIEWS_PAGE ? REVIEWS_PER_STANDARD_PAGE : REVIEWS_PER_WIDGET_PAGE;
    userFacingQuery.skip = skip;
    userFacingQuery.take = take;
    store.dispatch(
      loadMoreReviews({
        directory,
        skip,
        take,
        page,
        widget,
      })
    );
  }

  await waitForSaga(store);

  // Handle errors
  const errorCode = getErrorStatusCode(store, directory);
  const profile = selectProfile(store.getState(), directory);

  // Check for redirects
  const redirect = checkForVendorReviewsRedirect(profile, redirectQuery);
  if (redirect) {
    return redirect;
  }

  const pageAnalytics = buildPageAnalytics(
    PROFILE_REVIEWS_PAGE_NAME,
    profileReviewsPageViewProps(profile)
  );

  return {
    ...pageAnalytics,
    directory,
    query: userFacingQuery,
    page,
    pageType,
    errorCode,
  };
};

export const getServerSideProps = configureStore.getServerSideProps(
  (store) => async (ctx) =>
    await getServerSidePropsWrapper({ ...ctx, store }, handleServerSideProps)
);

const mapStateToProps = (state, ownProps) => {
  if (ownProps.errorCode) return {};

  return {
    profile: selectProfile(state, ownProps.directory),
  };
};

export default connect(mapStateToProps)(Reviews);
